type PaginatedAPI<T> = (page: number, pageSize: number) => Promise<T[]>;

type APIData<T> = {
    records: T[];
    page: number;
    hasReachedEnd: boolean;
};

type RecordComparator<T> = (a: T, b: T) => boolean;

function createInitialAPIState<T>(): APIData<T> {
    return {
        records: [],
        page: 1,
        hasReachedEnd: false,
    };
}

function needsToLoadMore<T>(cache: T[], pageSize: number) {
    return cache.length <= pageSize;
}

async function fetchRecords<T>(apiFetchFunction: PaginatedAPI<T>, apiData: APIData<T>, pageSize: number) {
    if (apiData.hasReachedEnd) {
        return;
    }
    if (needsToLoadMore(apiData.records, pageSize)) {
        try {
            const newRecords = await apiFetchFunction(apiData.page, pageSize);
            // eslint-disable-next-line no-param-reassign
            apiData.page += 1;
            if (newRecords.length < pageSize) {
                // eslint-disable-next-line no-param-reassign
                apiData.hasReachedEnd = true;
            }
            apiData.records.push(...newRecords);
        } catch {}
    }
}

function findNFittest<T>(aRecords: T[], bRecords: T[], pageSize: number, recordComparator: RecordComparator<T>) {
    if (aRecords.length === 0) {
        return bRecords.splice(0, pageSize);
    }
    if (bRecords.length === 0) {
        return aRecords.splice(0, pageSize);
    }
    const fittestRecords: T[] = [];

    while (fittestRecords.length < pageSize && (aRecords.length > 0 || bRecords.length > 0)) {
        const a = aRecords[0];
        const b = bRecords[0];
        const aIsFitter = recordComparator(a, b);
        fittestRecords.push(aIsFitter ? aRecords.shift()! : bRecords.shift()!);
    }
    return fittestRecords;
}

export function createMergedAPI<T>(aAPI: PaginatedAPI<T>, bAPI: PaginatedAPI<T>, recordComparator: RecordComparator<T>): {
    mergedFetch: (_: number, pageSize: number) => Promise<T[]>;
    restore: () => void;
} {
    let aData = createInitialAPIState<T>();
    let bData = createInitialAPIState<T>();

    function restore() {
        aData = createInitialAPIState<T>();
        bData = createInitialAPIState<T>();
    }

    /* Cannot jump to a specific page. Must start from page 1 and go up */
    async function mergedFetch(_: number, pageSize: number) {
        const aFetchRecords = fetchRecords(aAPI, aData, pageSize);
        const bFetchRecords = fetchRecords(bAPI, bData, pageSize);

        await Promise.all([aFetchRecords, bFetchRecords]);
        return findNFittest(aData.records, bData.records, pageSize, recordComparator);
    }

    return {
        mergedFetch,
        restore,
    };
}
